import { dropTask, all } from 'ember-concurrency';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import { htmlSafe } from '@ember/template';
import * as v from 'valibot';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedArray } from '@ember/reactive/collections';
import Component from '@glimmer/component';
import errorsForField from 'tio-common/helpers/errors-for-field';
import Input from 'tio-ui/components/input';
import TioDialogDocumentAsset from 'tio-common/components/tio/dialog/document-asset';
import type { IntlService } from 'ember-intl';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type { EmailAddressExtension as EmailAddressModel } from 'tio-data/data/resources/email-address.ext';
import type Errors from 'tio-common/types/errors';
import type Owner from '@ember/owner';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type RouterService from '@ember/routing/router';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import { contains } from 'tio-ui/utilities';
import Checkbox from 'tio-ui/components/checkbox';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

const postAuthRegisterFormSchema = v.object({
  didAcceptAllTerms: v.pipe(
    v.boolean('The terms and conditions must be accepted.'),
    v.check((val) => val === true, 'The terms and conditions must be accepted.')
  ),
});

export interface AuthRegisterPostAuthTermsDialogSignature {
  Args: {
    primaryEmailAddress?: EmailAddressModel;
    didSave?: () => void;
  };
}

export default class AuthRegisterPostAuthTermsDialogComponent extends Component<AuthRegisterPostAuthTermsDialogSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare preRegister: PreRegisterService;
  @service declare sessionContext: SessionContextService;

  @tracked hasSubmitted = false;
  @tracked submitError?: Errors;
  @tracked email = '';
  @tracked didAgreeAppTermsAndPrivacy = false;
  @tracked showingTermsOfUse = false;
  @tracked showingPrivacyPolicy = false;

  acceptedCustomTerms: ConditionModel[] = trackedArray([]);

  constructor(owner: Owner, args: AuthRegisterPostAuthTermsDialogSignature['Args']) {
    super(owner, args);

    if (this.args.primaryEmailAddress) {
      this.email = this.args.primaryEmailAddress.email;
    }

    // Pre-check the app terms checkbox if both are already accepted
    if (this.hasAcceptedTermsOfUse && this.hasAcceptedPrivacyPolicy) {
      this.didAgreeAppTermsAndPrivacy = true;
    }
  }

  get user(): UserModel {
    return this.sessionContext.user;
  }

  get isSsoRegistration() {
    return this.sessionContext.isOtherProviderLogin;
  }

  get needsEmailInput() {
    return this.isSsoRegistration && !this.args.primaryEmailAddress?.email;
  }

  get termsOfUse() {
    return this.preRegister.termsOfUse;
  }

  get privacyPolicy() {
    return this.preRegister.privacyPolicy;
  }

  get termsOfUseContent() {
    return this.termsOfUse?.fullText;
  }

  get privacyPolicyContent() {
    return this.privacyPolicy?.fullText;
  }

  // --- Already-accepted checks ---

  get hasAcceptedTermsOfUse() {
    return !!this.user?.usageAgreement;
  }

  get hasAcceptedPrivacyPolicy() {
    return !!this.user?.privacyAgreement;
  }

  get needsAppTermsCheckbox() {
    return !this.hasAcceptedTermsOfUse || !this.hasAcceptedPrivacyPolicy;
  }

  /** Custom terms the user has NOT yet agreed to */
  get customTerms(): ConditionModel[] {
    const allCustom = this.preRegister.customTerms || [];
    const agreedIds = new Set(
      // @ts-expect-error: relation typing
      (this.user?.customRegisterAgreements || []).map((a: AgreementModel) => a.condition?.id)
    );
    return allCustom.filter((c) => !agreedIds.has(c.id));
  }

  get didAcceptAllCustomTerms() {
    return this.acceptedCustomTerms.length === this.customTerms.length;
  }

  get didAcceptAllTerms() {
    return this.didAgreeAppTermsAndPrivacy && this.didAcceptAllCustomTerms;
  }

  /** Only the conditions the user still needs to agree to */
  get allConditionsToAccept(): ConditionModel[] {
    const missing: ConditionModel[] = [];
    if (!this.hasAcceptedTermsOfUse && this.termsOfUse) {
      missing.push(this.termsOfUse);
    }
    if (!this.hasAcceptedPrivacyPolicy && this.privacyPolicy) {
      missing.push(this.privacyPolicy);
    }
    missing.push(...this.customTerms);
    return missing;
  }

  get formModel() {
    return {
      email: this.email,
      didAcceptAllTerms: this.didAcceptAllTerms,
    };
  }

  get formSchema() {
    if (this.needsEmailInput) {
      return v.object({
        didAcceptAllTerms: v.pipe(
          v.boolean('The terms and conditions must be accepted.'),
          v.check((val) => val === true, 'The terms and conditions must be accepted.')
        ),
        email: v.pipe(
          v.string('Email is required'),
          v.minLength(1, '* Email is required'),
          v.email('Please enter a valid email address')
        ),
      });
    }
    return postAuthRegisterFormSchema;
  }

  get formValidationErrors() {
    const schema = this.formSchema;

    try {
      v.parse(schema, this.formModel);
    } catch (err) {
      if (err instanceof v.ValiError) {
        return err.issues || [];
      }
      return [];
    }
    return [];
  }

  get serverErrorMessages() {
    const genericErrorMessage = this.intl.t('error_occurred');
    if (this.submitError?.errors) {
      return this.submitError.errors.map((err) => {
        return err?.detail || genericErrorMessage;
      });
    } else if (this.submitError) {
      return [genericErrorMessage];
    }
    return [];
  }

  didChangeAcceptanceOfCustomTerm = (condition: ConditionModel, value: boolean) => {
    if (value) {
      this.acceptedCustomTerms.push(condition);
    } else {
      const index = this.acceptedCustomTerms.indexOf(condition);
      if (index !== -1) {
        this.acceptedCustomTerms.splice(index, 1);
      }
    }
  };

  showDialog = (type: string, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    switch (type) {
      case 'terms':
        this.showingTermsOfUse = true;
        break;
      case 'privacy':
        this.showingPrivacyPolicy = true;
        break;
    }
  };

  submitTask = dropTask(async () => {
    delete this.submitError;
    this.hasSubmitted = true;
    if (this.formValidationErrors.length) {
      return;
    }
    try {
      await this.saveFormValues.perform();
      this.hasSubmitted = false;
      await this.args.didSave?.();
    } catch (e) {
      this.submitError = e;
      console.error(e);
    }
  });

  saveFormValues = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.allConditionsToAccept.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });

    const emailAddress =
      this.args.primaryEmailAddress ||
      this.store.createRecord<EmailAddressModel>('email-address', {
        contactType: 'PRIMARY',
        user: user,
      });
    emailAddress.email = this.email;

    const promises = agreements.map((agreement) => {
      return saveRecord(this.store, agreement);
    });
    promises.push(saveRecord(this.store, emailAddress));
    const saved = await all(promises);
    return saved;
  });

  <template>
    <Modal
      @isOpen={{true}}
      @onClose={{this.submitTask.perform}}
      @size="xl"
      @allowCloseButton={{false}}
    >
      <:body>
        <div class="tio-stack-center">
          <h2 class="text-4xl mb-4">
            {{if this.needsEmailInput (t "login.sign_up") (t "login.terms_of_service_heading")}}
          </h2>

          <h2 class="text-xl mb-6 text-center">
            {{if
              this.needsEmailInput
              (t "login.enroll_in_tuition_to_access")
              (t "login.please_accept_the_terms")
            }}
          </h2>
        </div>

        <form class="tio-stack-center my-4">
          {{#if this.needsEmailInput}}
            <div class="max-w-xl">
              <Input
                data-test-id="email-input"
                @label={{t "login.email"}}
                @value={{this.email}}
                @onChange={{fn (mut this.email)}}
                @errors={{errorsForField "email" schemaErrors=this.formValidationErrors}}
              />
              <div class="text-sm tio-fg-muted my-2">
                {{t "login.please_use_email_for_notifications"}}
              </div>
            </div>
          {{/if}}

          <div class="my-2">
            {{#if this.needsAppTermsCheckbox}}
              <div class="flex flex-row items-start my-2">
                <Checkbox
                  data-test-id="app-terms-checkbox"
                  @size="lg"
                  @checked={{this.didAgreeAppTermsAndPrivacy}}
                  @onChange={{fn (mut this.didAgreeAppTermsAndPrivacy)}}
                  aria-labelledby="appTermsAndPrivacy"
                />
                <div id="appTermsAndPrivacy" class="ml-1">
                  {{t "login.i_have_read_and_agree_to_tuitions"}}
                  <button
                    class="tio-copy"
                    type="button"
                    {{on "click" (fn this.showDialog "terms")}}
                  >
                    {{t "login.terms_of_service"}}
                  </button>
                  {{t "and"}}
                  <button
                    class="tio-copy"
                    type="button"
                    {{on "click" (fn this.showDialog "privacy")}}
                  >
                    {{t "login.privacy_policy"}}.
                  </button>
                </div>
              </div>
            {{/if}}

            {{#each this.customTerms as |condition|}}
              <div class="flex flex-row items-start my-2">
                <Checkbox
                  data-test-id="custom-terms-checkbox"
                  @size="lg"
                  @checked={{contains this.acceptedCustomTerms condition}}
                  @onChange={{fn this.didChangeAcceptanceOfCustomTerm condition}}
                  aria-labelledby={{condition.id}}
                />
                <div id={{condition.id}} class="ml-1 tio-terms-and-conditions">
                  {{htmlSafe condition.fullText}}
                </div>
              </div>
            {{/each}}
          </div>
        </form>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submitTask.isRunning}}
          @onSubmit={{this.submitTask.perform}}
          @submitDisabled={{if this.formValidationErrors.length true false}}
          @submitText={{t "login.agree_text"}}
        />
      </:footer>
    </Modal>

    <TioDialogDocumentAsset
      @title="Privacy Policy"
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.privacyPolicy.staticJsonAssetName}}
      @content={{this.privacyPolicyContent}}
      @show={{this.showingPrivacyPolicy}}
      @onCancel={{fn (mut this.showingPrivacyPolicy) false}}
    />

    <TioDialogDocumentAsset
      @title="Terms of Service"
      {{! @glint-expect-error: should privacy be optional? }}
      @assetName={{this.termsOfUse.staticJsonAssetName}}
      @content={{this.termsOfUseContent}}
      @show={{this.showingTermsOfUse}}
      @onCancel={{fn (mut this.showingTermsOfUse) false}}
    />
  </template>
}
